import { useState } from "react";
import { Printer } from "lucide-react";

import { Button } from "../../components/ui/button";
import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { FilterBar, FilterField, Money } from "../forex/parts";
import { STATEMENT_LABEL, printHtml, useLedger, useLookups } from "./api";

const FP = "financial_position";
const PL = "profit_and_loss";
const REPORTS = [
  { value: FP, label: "Financial Position" },
  { value: PL, label: "Profit & Loss" },
];
const EMPTY = { fiscalYear: "", startDate: "", endDate: "", costCenter: "", receiptType: "", includeZero: "" };
const ZERO_OPTIONS = [
  { value: "", label: "Accounts with movement" },
  { value: "true", label: "Every active account" },
];
const COLUMNS = [
  { label: "Code" },
  { label: "Account" },
  { label: "Side" },
  { label: "Debit", align: "right" },
  { label: "Credit", align: "right" },
  { label: "Balance", align: "right" },
];

function Total({ label, value }) {
  return (
    <div className="space-y-1 rounded-lg border border-border/70 bg-white/[0.02] p-4">
      <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">{label}</div>
      <div className="text-xl">
        <Money value={value} />
      </div>
    </div>
  );
}

// One heading of the statement: its accounts and its own totals. On the
// Financial Position an account lands here by the side of its net balance.
function Section({ title, accounts, totals, muted }) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className={`text-sm font-medium ${muted ? "text-muted-foreground" : ""}`}>{title}</h3>
        <span className="font-mono text-xs text-muted-foreground">
          Dr {totals.debit} · Cr {totals.credit} · <Money value={totals.balance} />
        </span>
      </div>
      <QueueTable
        columns={COLUMNS}
        rows={accounts}
        emptyText="No accounts under this heading."
        renderRow={(a) => (
          <tr key={a.account} className="hover:bg-white/[0.02]">
            <Td className="font-mono">{a.code}</Td>
            <Td>{a.name}</Td>
            <Td className="text-xs capitalize text-muted-foreground">{a.balanceNature}</Td>
            <Td align="right" className="font-mono">{a.debit}</Td>
            <Td align="right" className="font-mono">{a.credit}</Td>
            <Td align="right"><Money value={a.balance} /></Td>
          </tr>
        )}
      />
    </div>
  );
}

const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

// The backend exports the account statement only, so the printable page for
// this report is built here from the same data the screen shows.
function printable(d, filters) {
  const section = (title, s) =>
    `<h2>${esc(title)}</h2><table><thead><tr><th>Code</th><th>Account</th><th>Side</th><th class="r">Debit</th><th class="r">Credit</th><th class="r">Balance</th></tr></thead><tbody>` +
    s.accounts
      .map((a) => `<tr><td>${esc(a.code)}</td><td>${esc(a.name)}</td><td>${esc(a.balanceNature)}</td><td class="r">${esc(a.debit)}</td><td class="r">${esc(a.credit)}</td><td class="r">${esc(a.balance)}</td></tr>`)
      .join("") +
    `</tbody><tfoot><tr><th colspan="3">Total</th><th class="r">${esc(s.totals.debit)}</th><th class="r">${esc(s.totals.credit)}</th><th class="r">${esc(s.totals.balance)}</th></tr></tfoot></table>`;
  const period = [filters.startDate && `from ${filters.startDate}`, filters.endDate && `to ${filters.endDate}`].filter(Boolean).join(" ");
  return (
    `<!doctype html><html><head><meta charset="utf-8"><title>${esc(STATEMENT_LABEL[d.report])} · ${esc(d.fiscalYear.title)}</title>` +
    `<style>body{font:12px Arial,sans-serif;color:#111;margin:24px}h1{font-size:20px}h2{font-size:14px;margin:18px 0 6px}table{width:100%;border-collapse:collapse}th,td{border-bottom:1px solid #ccc;padding:5px;text-align:left}th{background:#eee}.r{text-align:right}thead{display:table-header-group}tr{break-inside:avoid}@page{size:A4;margin:12mm}</style></head><body>` +
    `<h1>${esc(STATEMENT_LABEL[d.report])}</h1><p>${esc(d.fiscalYear.title)}${period ? ` · ${esc(period)}` : ""} · amounts in ${esc(d.baseCurrency)}</p>` +
    `<p>Debit ${esc(d.totals.debit)} | Credit ${esc(d.totals.credit)} | Balance ${esc(d.totals.balance)}</p>` +
    d.headings.filter((h) => h.accounts.length).map((h) => section(`${h.position}. ${h.name}`, h)).join("") +
    (d.unassigned.accounts.length ? section("Without a heading", d.unassigned) : "") +
    `</body></html>`
  );
}

export default function TrustLedgerFinancialStatement() {
  const lk = useLookups();
  const [report, setReport] = useState(FP);
  const [draft, setDraft] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);

  const fiscalYear = applied.fiscalYear || lk.openYear?._id;
  const q = useLedger(fiscalYear ? "/reports/statement" : null, { ...applied, fiscalYear, report });
  const d = q.data;
  const base = d?.baseCurrency ?? lk.baseCurrency;
  const shown = d?.headings.filter((h) => h.accounts.length) ?? [];
  const emptyHeadings = d?.headings.filter((h) => !h.accounts.length) ?? [];

  return (
    <QueueLayout
      eyebrow="Trust Ledger"
      title="Financial"
      accent="statements"
      description={`Closing balances per account grouped under report headings, in ${base}. On the Financial Position a debit balance sits under the account's debit heading and a credit balance under its credit heading.`}
      statusOptions={REPORTS}
      status={report}
      onStatus={setReport}
      isFetching={q.isFetching}
      onRefresh={() => q.refetch()}
    >
      <FilterBar
        onApply={() => setApplied(draft)}
        onReset={() => {
          setDraft(EMPTY);
          setApplied(EMPTY);
        }}
      >
        <FilterField
          label="Fiscal year"
          name="fiscalYear"
          options={[
            { value: "", label: "Open year" },
            ...lk.years.map((y) => ({ value: y._id, label: `${y.title} (${y.status})` })),
          ]}
          draft={draft}
          setDraft={setDraft}
        />
        <FilterField label="From date" name="startDate" type="date" draft={draft} setDraft={setDraft} />
        <FilterField label="To date" name="endDate" type="date" draft={draft} setDraft={setDraft} />
        <FilterField
          label="Cost center"
          name="costCenter"
          options={[{ value: "", label: "All" }, ...lk.costCenters.map((c) => ({ value: c._id, label: `${c.code} · ${c.name}` }))]}
          draft={draft}
          setDraft={setDraft}
        />
        <FilterField
          label="Voucher type"
          name="receiptType"
          options={[{ value: "", label: "All" }, ...(lk.options?.receiptTypes ?? []).map((t) => ({ value: t, label: t }))]}
          draft={draft}
          setDraft={setDraft}
        />
        <FilterField label="Show" name="includeZero" options={ZERO_OPTIONS} draft={draft} setDraft={setDraft} />
      </FilterBar>

      {q.error ? (
        <ErrorBanner error={q.error} onRetry={() => q.refetch()} />
      ) : !d ? (
        <p className="text-sm text-muted-foreground">{fiscalYear ? "Loading…" : "No fiscal year yet."}</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {d.fiscalYear.title} · {STATEMENT_LABEL[d.report]} · base currency {d.baseCurrency}
            </p>
            <Button size="sm" onClick={() => printHtml(printable(d, applied))} disabled={!shown.length && !d.unassigned.accounts.length}>
              <Printer className="h-3.5 w-3.5" /> Print / PDF
            </Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Total label={`Debit (${base})`} value={d.totals.debit} />
            <Total label={`Credit (${base})`} value={d.totals.credit} />
            <Total label={`Balance (${base})`} value={d.totals.balance} />
          </div>
          {shown.map((h) => (
            <Section key={h._id} title={`${h.position}. ${h.name}`} accounts={h.accounts} totals={h.totals} />
          ))}
          {d.unassigned.accounts.length > 0 && (
            <Section title="Without a heading" accounts={d.unassigned.accounts} totals={d.unassigned.totals} muted />
          )}
          {!shown.length && !d.unassigned.accounts.length && (
            <p className="py-12 text-center text-sm text-muted-foreground">No {STATEMENT_LABEL[d.report]} balances match.</p>
          )}
          {emptyHeadings.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Headings with nothing under them: {emptyHeadings.map((h) => h.name).join(", ")}.
            </p>
          )}
        </>
      )}
    </QueueLayout>
  );
}
